import React, { memo } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';

import {
  useSessionLinks,
  useIsAdmin,
  ParticipationSource,
  Participation,
} from '@app/api';
import { Chip, ChipColor, TooltipText } from 'components';
import Logo from '@app/illu/Branding/SmallLogoBux.svg';

const useStyles = createUseStyles((theme: Theme) => ({
  chip: {
    marginRight: theme.marginBase,
  },
  chipWithIcon: {
    width: theme.marginBase * 3,
    height: theme.marginBase * 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tandemzIcon: {
    paddingBottom: '2px',
  },
  chipIcon: {
    fontSize: theme.iconSize.small,
    fontWeight: 'normal',
  },
  chipWithIconText: {
    marginTop: theme.marginBase,
    ...theme.fonts.calendarText,
    color: theme.colors.inkTertiary,
  },
  logoImg: {
    height: theme.height.chips,
  },
}));

interface Props {
  participation: Participation;
  className?: string;
}

export const participationSourceColors: { [source: string]: ChipColor } = {
  [ParticipationSource.LINK]: 'blue',
  [ParticipationSource.ADS]: 'purple',
  [ParticipationSource.INVITE]: 'green',
  [ParticipationSource.TANDEMZ_PANEL]: 'yellow',
  [ParticipationSource.REFERRAL]: 'red',
  [ParticipationSource.MANUAL_RECRUIT]: 'purple',
};

export const ParticipationSourceChips = memo(
  function ParticipationSourceChipsContent(props: Props) {
    const theme = useTheme() as Theme;
    const classes = useStyles({ theme });

    const isAdmin = useIsAdmin();
    const { t } = useTranslation();

    const { data: sessionLinks } = useSessionLinks(
      props.participation.sessionId,
    );

    const { source, sourceDetails, sourcePlatform } = props.participation;
    const color = participationSourceColors[source];

    if (isAdmin) {
      return (
        <>
          <Chip
            color={color}
            className={classnames([classes.chip, props.className])}
          >
            {t(`sessions.details.monitoring.sources.${source}`)}
          </Chip>
          {sourcePlatform && (
            <Chip
              color="blue"
              className={classnames([classes.chip, props.className])}
            >
              {sourcePlatform}
            </Chip>
          )}
          {sourceDetails && (
            <Chip
              color="ink"
              className={classnames([classes.chip, props.className])}
            >
              {sourceDetails}
            </Chip>
          )}
        </>
      );
    }

    const sessionLink = sessionLinks?.find(
      (item) => item.id === props.participation.sessionLinkId,
    );

    const isRecruitmentByTandemz = sessionLinks
      ? !!sessionLink?.recruitmentByTandemz
      : source !== ParticipationSource.LINK &&
        source !== ParticipationSource.INVITE;

    if (isRecruitmentByTandemz) {
      return (
        <TooltipText title={t('sessions.details.monitoring.chips.fromTandemz')}>
          <img
            src={Logo}
            alt="Bilendi UX Logo"
            className={classnames(props.className, classes.logoImg)}
          />
        </TooltipText>
      );
    }

    return (
      <Chip
        color="inkSecondary"
        className={classnames(props.className, classes.chipWithIcon)}
      >
        <TooltipText
          title={t('sessions.details.monitoring.chips.fromPublicLink')}
        >
          <i className={classnames(classes.chipIcon, 'ri-link-m')} />
        </TooltipText>
      </Chip>
    );
  },
);
